import {
    ConfirmRequestResult,
    CreateReservationResult,
    DeclineRequestResult,
    ReservationDTO,
    Reservations,
    SaveRequestResult,
    UpdateReservationModel,
} from 'api/controllers/reservations';

import { AppThunkAction, AppThunkActionAsync } from '../index';
import globalStore from '../globalStore';
import { ReservationsAction } from './actions';

let requestsLoad: { restaurantId: string; promise: Promise<boolean> } | null = null;

export const actionCreators = {
    getRequests:
        (restaurantId: string): AppThunkActionAsync<ReservationsAction, boolean> =>
        (dispatch, getState) => {
            const isStillCurrent = () => getState?.().user.currentRestaurantId === restaurantId;
            const promise = Reservations.getRequests()
                .then((requests) => {
                    if (!isStillCurrent()) {
                        return false;
                    }
                    dispatch({
                        type: 'RESERVATION_REQUESTS_SET',
                        restaurantId,
                        requests,
                    });
                    return true;
                })
                .catch((e) => {
                    if (isStillCurrent()) {
                        globalStore.actionCreators.showToaster('error', e)(dispatch);
                    }
                    return false;
                })
                .finally(() => {
                    if (requestsLoad?.promise === promise) {
                        requestsLoad = null;
                    }
                });
            requestsLoad = { restaurantId, promise };
            return promise;
        },

    ensureRequests:
        (restaurantId: string): AppThunkActionAsync<ReservationsAction, boolean> =>
        (dispatch, getState) => {
            if (getState?.().reservations.requestsRestaurantId === restaurantId) {
                return Promise.resolve(true);
            }
            if (requestsLoad?.restaurantId === restaurantId) {
                return requestsLoad.promise;
            }
            return actionCreators.getRequests(restaurantId)(dispatch, getState);
        },

    selectRequest: (id: string | null): ReservationsAction => ({
        type: 'RESERVATION_REQUEST_SELECT',
        id,
    }),

    selectActive: (id: string | null): ReservationsAction => ({
        type: 'RESERVATION_ACTIVE_SELECT',
        id,
    }),

    getActiveReservations:
        (day: string | null): AppThunkAction<ReservationsAction> =>
        (dispatch) => {
            dispatch({ type: 'RESERVATION_ACTIVE_LOAD', day });
            Reservations.getActiveReservations(day)
                .then((reservations) =>
                    dispatch({ type: 'RESERVATION_ACTIVE_SET', day, reservations })
                )
                .catch((e) => globalStore.actionCreators.showToaster('error', e)(dispatch));
        },

    getTablesAvailability:
        (
            start: string,
            durationMinutes: number,
            excludeReservationId: string | null
        ): AppThunkAction<ReservationsAction> =>
        (dispatch) => {
            const key = [start, durationMinutes, excludeReservationId].join('|');
            dispatch({ type: 'RESERVATION_AVAILABILITY_LOAD', key });
            Reservations.getTablesAvailability(start, durationMinutes, excludeReservationId)
                .then((tables) => dispatch({ type: 'RESERVATION_AVAILABILITY_SET', key, tables }))
                .catch((e) => globalStore.actionCreators.showToaster('error', e)(dispatch));
        },

    clearTablesAvailability: (): ReservationsAction => ({
        type: 'RESERVATION_AVAILABILITY_CLEAR',
    }),

    saveRequest:
        (
            id: string,
            model: UpdateReservationModel
        ): AppThunkActionAsync<ReservationsAction, SaveRequestResult> =>
        async (dispatch) => {
            const result = await Reservations.saveRequest(id, model);
            if (result.status === 'saved') {
                dispatch({ type: 'RESERVATION_REQUEST_SAVED', reservation: result.reservation });
            } else {
                dispatch({ type: 'RESERVATION_REQUEST_REMOVED', id });
            }
            return result;
        },

    confirmRequest:
        (
            id: string,
            model: UpdateReservationModel
        ): AppThunkActionAsync<ReservationsAction, ConfirmRequestResult> =>
        async (dispatch) => {
            const result = await Reservations.confirmRequest(id, model);
            if (result.status === 'confirmed') {
                dispatch({ type: 'RESERVATION_CONFIRMED', reservation: result.reservation });
            } else if (result.status === 'alreadyHandled') {
                dispatch({ type: 'RESERVATION_REQUEST_REMOVED', id });
            }
            return result;
        },

    declineRequest:
        (id: string): AppThunkActionAsync<ReservationsAction, DeclineRequestResult> =>
        async (dispatch, getState) => {
            const result = await Reservations.declineRequest(id);
            if (result === 'declined') {
                dispatch({ type: 'RESERVATION_REQUEST_REMOVED', id });
            } else if (getState) {
                await actionCreators.getRequests(getState().user.currentRestaurantId)(
                    dispatch,
                    getState
                );
            }
            return result;
        },

    createReservation:
        (
            model: UpdateReservationModel
        ): AppThunkActionAsync<ReservationsAction, CreateReservationResult> =>
        async (dispatch) => {
            const result = await Reservations.createReservation(model);
            if (result.status === 'created') {
                dispatch({ type: 'RESERVATION_CONFIRMED', reservation: result.reservation });
            }
            return result;
        },

    applyRealtimeUpdate: (reservation: ReservationDTO): ReservationsAction => ({
        type: 'RESERVATION_REALTIME_UPDATE',
        reservation,
    }),
};
